'use client'

import * as React from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { Command } from 'cmdk'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { getVisibleNavItems } from '@/lib/navigation'
import { useAI } from '@/context/AIContext'

interface CommandPaletteContextValue {
  open: boolean
  setOpen: (open: boolean) => void
}

const CommandPaletteContext = React.createContext<CommandPaletteContextValue | null>(null)

export function useCommandPalette(): { openPalette: () => void } {
  const ctx = React.useContext(CommandPaletteContext)
  return {
    openPalette: React.useCallback(() => ctx?.setOpen(true), [ctx]),
  }
}

export function CommandPaletteProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false)
  const value = React.useMemo(() => ({ open, setOpen }), [open])
  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
    </CommandPaletteContext.Provider>
  )
}

interface CommandPaletteProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

interface CommandPaletteItem {
  id: string
  label: string
  icon: React.ElementType
  href?: string
  action?: 'refresh' | 'open-ai-manager'
  keywords?: string[]
  hint?: string
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const ctx = React.useContext(CommandPaletteContext)
  const [isOpen, setIsOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const router = useRouter()
  const pathname = usePathname()
  const { data: session } = useSession()
  const { openManager } = useAI()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const isAdmin = !!session?.roles?.includes('admin')

  const controlledOpen = ctx ? ctx.open : open !== undefined ? open : isOpen
  const setControlledOpen = ctx ? ctx.setOpen : onOpenChange ?? setIsOpen
  const navItems = React.useMemo(() => getVisibleNavItems(isAdmin), [isAdmin])

  const actionItems = React.useMemo<CommandPaletteItem[]>(() => {
    return [
      {
        id: 'open-ai-manager',
        label: 'Open AI Manager',
        icon: Icons.sparkles,
        action: 'open-ai-manager',
        keywords: ['assistant', 'supervity ai', 'chat'],
        hint: '⌘J',
      },
      {
        id: 'refresh-current-page',
        label: 'Refresh Current Page',
        icon: Icons.refresh,
        action: 'refresh',
        keywords: ['reload', 'refresh', 'sync'],
        hint: pathname,
      },
    ]
  }, [pathname])

  const paletteItems = React.useMemo<CommandPaletteItem[]>(() => {
    const navigationItems = navItems.map((item) => ({
      id: item.href,
      label: item.label,
      icon: item.icon,
      href: item.href,
      keywords: item.keywords,
      hint: item.href,
    }))

    return [...navigationItems, ...actionItems]
  }, [actionItems, navItems])

  // Keyboard shortcut handler
  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      // Cmd+K or Ctrl+K to toggle
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setControlledOpen(!controlledOpen)
      }
    }

    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [controlledOpen, setControlledOpen])

  // Focus input when opened and clear search when closed
  React.useEffect(() => {
    if (controlledOpen) {
      // Small delay to ensure element is rendered
      setTimeout(() => {
        inputRef.current?.focus()
      }, 0)
    } else {
      setSearch('')
    }
  }, [controlledOpen])

  const handleSelect = (item: CommandPaletteItem) => {
    setControlledOpen(false)
    setSearch('')

    if (item.href) {
      router.push(item.href)
    } else if (item.action) {
      switch (item.action) {
        case 'refresh':
          window.location.reload()
          break
        case 'open-ai-manager':
          openManager()
          break
      }
    }
  }

  return (
    <Dialog open={controlledOpen} onOpenChange={setControlledOpen}>
      <DialogContent className='overflow-hidden border border-white/40 bg-white/95 p-0 shadow-float-lg sm:max-w-2xl [&>button]:hidden'>
        <DialogTitle className='sr-only'>Command Palette</DialogTitle>
        <Command
          className={cn(
            'rounded-2xl',
            'bg-white/95 backdrop-blur-xl',
            'overflow-hidden'
          )}
          loop
        >
          {/* Search Input */}
          <div className='flex items-center border-b border-border/50 px-4'>
            <Icons.search className='h-4 w-4 shrink-0 text-muted-foreground' />
            <Command.Input
              ref={inputRef}
              value={search}
              onValueChange={setSearch}
              placeholder='Jump to pages, tools, and actions...'
              className={cn(
                'h-14 flex-1 px-3',
                'bg-transparent text-foreground',
                'placeholder:text-muted-foreground',
                'focus:outline-none'
              )}
            />
            <kbd className='hidden h-6 items-center rounded border border-border/50 bg-muted/50 px-2 text-xs text-muted-foreground sm:inline-flex'>
              ESC
            </kbd>
          </div>

          {/* Results */}
          <Command.List
            className='max-h-[min(70vh,460px)] overflow-y-auto [scrollbar-gutter:stable] p-2'
            aria-live='polite'
          >
            <Command.Empty className='py-6 text-center text-sm text-muted-foreground'>
              No results found.
            </Command.Empty>

            {paletteItems.map((item) => (
              <Command.Item
                key={item.id}
                value={item.label}
                keywords={item.keywords}
                onSelect={() => handleSelect(item)}
                className={cn(
                  'group relative flex cursor-pointer items-center gap-3 rounded-xl border border-transparent px-3 py-2.5 outline-none',
                  'text-sm text-foreground',
                  'hover:border-slate-200/80 hover:bg-slate-50',
                  'aria-selected:border-brand-navy/80 aria-selected:bg-brand-navy aria-selected:text-white aria-selected:shadow-md aria-selected:shadow-brand-navy/20',
                  'data-[selected=true]:border-brand-navy/80 data-[selected=true]:bg-brand-navy data-[selected=true]:text-white data-[selected=true]:shadow-md data-[selected=true]:shadow-brand-navy/20',
                  'transition-[background-color,border-color,color,box-shadow]'
                )}
              >
                <item.icon
                  className='h-4 w-4 text-muted-foreground/80 transition-colors group-aria-selected:text-white group-data-[selected=true]:text-white'
                  strokeWidth={1.5}
                />
                <span className='flex-1 font-medium'>{item.label}</span>
                {item.href === pathname ? (
                  <span className='rounded-full border border-brand-cornflower/20 bg-brand-cornflower/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-navy transition-colors group-aria-selected:border-white/25 group-aria-selected:bg-white/18 group-aria-selected:text-white group-data-[selected=true]:border-white/25 group-data-[selected=true]:bg-white/18 group-data-[selected=true]:text-white'>
                    Current
                  </span>
                ) : item.hint ? (
                  <span className='text-xs text-muted-foreground/80 transition-colors group-aria-selected:text-white/80 group-data-[selected=true]:text-white/80'>
                    {item.hint}
                  </span>
                ) : null}
              </Command.Item>
            ))}
          </Command.List>

          {/* Footer */}
          <div className='flex items-center justify-between border-t border-border/50 px-4 py-2 text-xs text-muted-foreground'>
            <div className='flex items-center gap-4'>
              <span className='flex items-center gap-1'>
                <kbd className='rounded border border-border/50 bg-muted/50 px-1'>
                  ↑↓
                </kbd>
                Navigate
              </span>
              <span className='flex items-center gap-1'>
                <kbd className='rounded border border-border/50 bg-muted/50 px-1'>
                  ↵
                </kbd>
                Select
              </span>
            </div>
            <span className='flex items-center gap-1'>
              <kbd className='rounded border border-border/50 bg-muted/50 px-1'>
                ESC
              </kbd>
              Close
            </span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  )
}
